import { Modal, Button, Typography } from "antd";
import { GoogleOutlined, LinkedinOutlined } from "@ant-design/icons";
import apiClient from "QueryClient/axiosClient";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "Constants/AllRoutes";
import { useUserInfo } from "GlobalStates/User";
import { useLoginWithGoogle } from "Hooks/useGoogleLogin";
import { getConfig } from "Config";

const { Title, Text } = Typography;

const loginUserWithGoogleToken = (data) => {
  return apiClient.post("/auth/googleLogin", data);
};

const LoginModalContent = ({
  open,
  onClose,
  onLinkedInLogin,
  isGoogleEnabled,
  onGoogleClick,
  isLinkedInEnabled,
}) => {
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={400}
      destroyOnClose
    >
      <Title level={4} style={{ textAlign: "center" }}>
        Sign in to your account
      </Title>

      <Text
        type="secondary"
        style={{ display: "block", textAlign: "center", marginBottom: 24 }}
      >
        Choose a provider to continue
      </Text>

      <Button
        block
        size="large"
        icon={<GoogleOutlined />}
        onClick={onGoogleClick}
        disabled={!isGoogleEnabled}
        style={{ marginBottom: 12 }}
      >
        Continue with Google
      </Button>
      {!isGoogleEnabled && (
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          Google login is disabled in this environment.
        </Text>
      )}

      <Button
        block
        size="large"
        icon={<LinkedinOutlined />}
        onClick={onLinkedInLogin}
        disabled={!isLinkedInEnabled}
        style={{ backgroundColor: "#0A66C2", color: "#fff" }}
      >
        Continue with LinkedIn
      </Button>
      {!isLinkedInEnabled && (
        <Text type="secondary" style={{ display: "block", marginTop: 12 }}>
          LinkedIn login is disabled in this environment.
        </Text>
      )}
    </Modal>
  );
};

const LoginModalWithGoogle = (props) => {
  const generateGoogleToken = useLoginWithGoogle({
    onSuccessCallback: async (user) => {
      try {
        await props.loginUserMutation.mutateAsync({
          token: user?.access_token,
        });
        props.onGoogleLogin();
      } catch (err) {
        console.log(err);
      }
    },
  });

  return (
    <LoginModalContent
      {...props}
      isGoogleEnabled
      onGoogleClick={() => generateGoogleToken()}
    />
  );
};

const LoginModalNoGoogle = (props) => {
  return (
    <LoginModalContent
      {...props}
      isGoogleEnabled={false}
      onGoogleClick={() => {}}
    />
  );
};

const LoginModal = ({ open, onClose, onGoogleLogin, onLinkedInLogin }) => {
  const { GoogleOAuthID } = getConfig();
  const isGoogleEnabled = Boolean(
    GoogleOAuthID && GoogleOAuthID !== "undefined" && GoogleOAuthID !== "null",
  );
  const isLinkedInEnabled = Boolean(
    process.env.REACT_APP_LINKEDIN_CLIENT_ID &&
    process.env.REACT_APP_LINKEDIN_CLIENT_ID !== "undefined" &&
    process.env.REACT_APP_LINKEDIN_CLIENT_ID !== "null",
  );
  const { setUser } = useUserInfo();
  const navigate = useNavigate();
  const loginUserMutation = useMutation({
    mutationFn: loginUserWithGoogleToken,
    onSuccess: (data) => {
      setUser({
        token: data?.data?.accessToken,
        name: data?.data?.name,
        role: data?.data?.role,
        hasCompletedProfile: data?.data?.hasCompletedProfile,
        isVerified: data?.data?.isVerified,
        picture: data?.data?.picture,
      });
      if (data?.data?.hasCompletedProfile) {
        // navigate(ROUTES.MY_CALENDAR);
        navigate(ROUTES.DASHBOARD_HOME);
      } else {
        navigate(ROUTES.REGISTER_ROLE);
      }
    },
  });

  const sharedProps = {
    open,
    onClose,
    onGoogleLogin,
    onLinkedInLogin,
    loginUserMutation,
    isLinkedInEnabled,
  };

  if (isGoogleEnabled) {
    return <LoginModalWithGoogle {...sharedProps} />;
  }
  return <LoginModalNoGoogle {...sharedProps} />;
};

export default LoginModal;
